import type { Component } from "vue";

import type { RouteLocation } from "@/router/types";

import ArenaIcon from "@/icons/brands/arena.svg";
import BigCommerceIcon from "@/icons/brands/bigcommerce.svg";
import BlueskyIcon from "@/icons/brands/bluesky.svg";
import BuySellAdsIcon from "@/icons/brands/buysellads.svg";
import DiscordIcon from "@/icons/brands/discord.svg";
import FathomIcon from "@/icons/brands/fathom.svg";
import GoogleIcon from "@/icons/brands/google.svg";
import LinkedInIcon from "@/icons/brands/linkedin.svg";
import MastodonIcon from "@/icons/brands/mastodon.svg";
import MemberfulIcon from "@/icons/brands/memberful.svg";
import MetaIcon from "@/icons/brands/meta.svg";
import PatreonIcon from "@/icons/brands/patreon.svg";
import PlausibleIcon from "@/icons/brands/plausible.svg";
import RedditIcon from "@/icons/brands/reddit.svg";
import SelineIcon from "@/icons/brands/seline.svg";
import ShopifyIcon from "@/icons/brands/shopify.svg";
import SimpleAnalyticsIcon from "@/icons/brands/simple-analytics.svg";
import SquarespaceIcon from "@/icons/brands/squarespace.svg";
import TinylyticsIcon from "@/icons/brands/tinylytics.svg";
import StripeIcon from "@/icons/brands/stripe.svg";
import TumblrIcon from "@/icons/brands/tumblr.svg";
import TwitterIcon from "@/icons/brands/twitter.svg";
import UmamiIcon from "@/icons/brands/umami.svg";
import WebflowIcon from "@/icons/brands/webflow.svg";
import ZapierIcon from "@/icons/brands/zapier.svg";
import type { Account } from "@/types/account";
import type { Newsletter } from "@/types/newsletter";
import type { Feature } from "../Billing/lib";

export type Integration = {
  name: string;
  description: string;
  brandIcon: Component;
  feature?: Feature;
  isConnected?: (newsletter: Newsletter, account: Account) => boolean;
} & (
  | { type: "internal"; to: RouteLocation }
  | { type: "external"; href: string }
);

export const ARENA_INTEGRATION: Integration = {
  type: "internal",
  name: "Arena",
  brandIcon: ArenaIcon,
  description:
    "Automatically post your emails to an Arena channel when you hit send.",
  to: { name: "/settings/integrations/arena" },
  isConnected(_newsletter, account) {
    return account.connections.some((c) => c.provider === "arena");
  },
};

export const BIGCOMMERCE_INTEGRATION: Integration = {
  type: "internal",
  name: "BigCommerce",
  brandIcon: BigCommerceIcon,
  description:
    "Sync your BigCommerce customers and orders with Buttondown subscribers.",
  to: { name: "/settings/integrations/bigcommerce" },
  isConnected(newsletter) {
    return (
      !!newsletter.bigcommerce_store_hash &&
      !!newsletter.bigcommerce_access_token
    );
  },
};

export const BLUESKY_INTEGRATION: Integration = {
  type: "internal",
  name: "Bluesky",
  brandIcon: BlueskyIcon,
  description:
    "Automatically share your emails as posts on Bluesky when you hit send.",
  to: { name: "/settings/integrations/bluesky" },
  isConnected(newsletter) {
    return !!newsletter.bluesky_username && !!newsletter.bluesky_api_key;
  },
};

export const BUYSELLADS_INTEGRATION: Integration = {
  type: "internal",
  name: "BuySellAds",
  brandIcon: BuySellAdsIcon,
  description:
    "Serve dynamic ads in your newsletter by connecting your BuySellAds zone.",
  to: { name: "/settings/integrations/buysellads" },
  isConnected(newsletter) {
    return !!newsletter.buysellads_url;
  },
};

export const DISCORD_INTEGRATION: Integration = {
  type: "internal",
  name: "Discord",
  brandIcon: DiscordIcon,
  description:
    "Get a notification in your Discord server whenever you send an email.",
  to: { name: "/settings/integrations/discord" },
  isConnected(_newsletter, account) {
    return account.connections.some((c) => c.provider === "discord_webhook");
  },
};

export const FATHOM_INTEGRATION: Integration = {
  type: "internal",
  name: "Fathom",
  brandIcon: FathomIcon,
  description:
    "Add privacy-friendly Fathom analytics to your newsletter's web pages.",
  to: { name: "/settings/integrations/fathom" },
  isConnected(newsletter) {
    return !!newsletter.fathom_code;
  },
};

export const GOOGLE_INTEGRATION: Integration = {
  type: "internal",
  name: "Google",
  brandIcon: GoogleIcon,
  description:
    "Add Google Tag Manager to your newsletter's web pages for analytics and tracking.",
  to: { name: "/settings/integrations/google" },
  isConnected(newsletter) {
    return !!newsletter.google_tag_manager_code;
  },
};

export const LINKEDIN_INTEGRATION: Integration = {
  type: "internal",
  name: "LinkedIn",
  brandIcon: LinkedInIcon,
  description:
    "Automatically share your emails as LinkedIn posts when you hit send.",
  to: { name: "/settings/integrations/linkedin" },
  isConnected(newsletter, account) {
    return account.connections.some((c) => c.provider === "linkedin");
  },
};

export const MASTODON_INTEGRATION: Integration = {
  type: "internal",
  name: "Mastodon",
  brandIcon: MastodonIcon,
  description:
    "Automatically share your emails as Mastodon posts when you hit send.",
  to: { name: "/settings/integrations/mastodon" },
  isConnected(newsletter) {
    return !!newsletter.mastodon_instance && !!newsletter.mastodon_api_key;
  },
};

export const MEMBERFUL_INTEGRATION: Integration = {
  type: "internal",
  name: "Memberful",
  brandIcon: MemberfulIcon,
  description:
    "Sync your Memberful members with Buttondown subscribers and gate content by plan.",
  to: { name: "/settings/integrations/memberful" },
  isConnected(newsletter) {
    return !!newsletter.memberful_api_key && !!newsletter.memberful_username;
  },
};

export const META_INTEGRATION: Integration = {
  type: "internal",
  name: "Meta",
  brandIcon: MetaIcon,
  description:
    "Add a Meta pixel to your newsletter's web pages for conversion tracking and retargeting.",
  to: { name: "/settings/integrations/meta" },
  isConnected(newsletter) {
    return !!newsletter.meta_pixel_id;
  },
};

export const PATREON_INTEGRATION: Integration = {
  type: "internal",
  name: "Patreon",
  brandIcon: PatreonIcon,
  description:
    "Sync your Patreon campaigns, tiers, and members with Buttondown subscribers.",
  to: { name: "/settings/integrations/patreon" },
  isConnected(_newsletter, account) {
    return account.connections.some((c) => c.provider === "patreon");
  },
};

export const PLAUSIBLE_INTEGRATION: Integration = {
  type: "internal",
  name: "Plausible",
  brandIcon: PlausibleIcon,
  description:
    "Add privacy-friendly Plausible analytics to your newsletter's web pages.",
  to: { name: "/settings/integrations/plausible" },
  isConnected(newsletter) {
    return !!newsletter.is_using_plausible;
  },
};

export const REDDIT_INTEGRATION: Integration = {
  type: "internal",
  name: "Reddit",
  brandIcon: RedditIcon,
  description:
    "Add a Reddit pixel to your newsletter's web pages to measure ad conversions.",
  to: { name: "/settings/integrations/reddit" },
  isConnected(newsletter) {
    return !!newsletter.reddit_code;
  },
};

export const SELINE_INTEGRATION: Integration = {
  type: "internal",
  name: "Seline",
  brandIcon: SelineIcon,
  description:
    "Add Seline analytics to your newsletter's web pages for lightweight tracking.",
  to: { name: "/settings/integrations/seline" },
  isConnected(newsletter) {
    return !!newsletter.seline_code;
  },
};

export const SHOPIFY_INTEGRATION: Integration = {
  type: "internal",
  name: "Shopify",
  brandIcon: ShopifyIcon,
  description:
    "Automatically subscribe customers and trigger automations based on Shopify orders.",
  to: { name: "/settings/integrations/shopify" },
  isConnected(newsletter) {
    return !!newsletter.shopify_api_key && !!newsletter.shopify_url;
  },
};

export const SIMPLE_ANALYTICS_INTEGRATION: Integration = {
  type: "internal",
  name: "Simple Analytics",
  brandIcon: SimpleAnalyticsIcon,
  description:
    "Add privacy-friendly Simple Analytics to your newsletter's web pages.",
  to: { name: "/settings/integrations/simple_analytics" },
  isConnected(newsletter) {
    return !!newsletter.simple_analytics_enabled;
  },
};

export const STRIPE_INTEGRATION: Integration = {
  type: "internal",
  name: "Stripe",
  brandIcon: StripeIcon,
  description:
    "Connect your Stripe account to accept payments and manage paid subscriptions.",
  to: { name: "/settings/integrations/stripe" },
  feature: "paid_subscriptions",
  isConnected(newsletter) {
    return !!newsletter.stripe_account;
  },
};

export const TINYLYTICS_INTEGRATION: Integration = {
  type: "internal",
  name: "Tinylytics",
  brandIcon: TinylyticsIcon,
  description:
    "Add lightweight Tinylytics analytics to your newsletter's web pages.",
  to: { name: "/settings/integrations/tinylytics" },
  isConnected(newsletter) {
    return !!newsletter.tinylytics_code;
  },
};

export const TUMBLR_INTEGRATION: Integration = {
  type: "internal",
  name: "Tumblr",
  brandIcon: TumblrIcon,
  description:
    "Automatically share your emails as Tumblr posts when you hit send.",
  to: { name: "/settings/integrations/tumblr" },
  isConnected(_newsletter, account) {
    return account.connections.some((c) => c.provider === "tumblr");
  },
};

export const TWITTER_INTEGRATION: Integration = {
  type: "internal",
  name: "Twitter",
  brandIcon: TwitterIcon,
  description: "Automatically share your emails as tweets when you hit send.",
  to: { name: "/settings/integrations/twitter" },
  isConnected(_newsletter, account) {
    return account.connections.some((c) => c.provider === "twitter_oauth2");
  },
};

export const UMAMI_INTEGRATION: Integration = {
  type: "internal",
  name: "Umami",
  brandIcon: UmamiIcon,
  description:
    "Add privacy-friendly Umami analytics to your newsletter's web pages.",
  to: { name: "/settings/integrations/umami" },
  isConnected(newsletter) {
    return !!newsletter.umami_website_id;
  },
};

export const ZAPIER_INTEGRATION: Integration = {
  type: "internal",
  name: "Zapier",
  brandIcon: ZapierIcon,
  description:
    "Connect Buttondown to thousands of other apps to automate your workflow.",
  to: { name: "/settings/integrations/zapier" },
  isConnected(_newsletter, account) {
    return account.tokens.some((t) => t.name === "zapier");
  },
};

export const INTEGRATION_ID_TO_CONFIGURATION = {
  arena: ARENA_INTEGRATION,
  bigcommerce: BIGCOMMERCE_INTEGRATION,
  bluesky: BLUESKY_INTEGRATION,
  buysellads: BUYSELLADS_INTEGRATION,
  discord: DISCORD_INTEGRATION,
  fathom: FATHOM_INTEGRATION,
  google: GOOGLE_INTEGRATION,
  linkedin: LINKEDIN_INTEGRATION,
  mastodon: MASTODON_INTEGRATION,
  memberful: MEMBERFUL_INTEGRATION,
  meta: META_INTEGRATION,
  patreon: PATREON_INTEGRATION,
  plausible: PLAUSIBLE_INTEGRATION,
  reddit: REDDIT_INTEGRATION,
  seline: SELINE_INTEGRATION,
  shopify: SHOPIFY_INTEGRATION,
  simple_analytics: SIMPLE_ANALYTICS_INTEGRATION,
  stripe: STRIPE_INTEGRATION,
  tinylytics: TINYLYTICS_INTEGRATION,
  tumblr: TUMBLR_INTEGRATION,
  twitter: TWITTER_INTEGRATION,
  umami: UMAMI_INTEGRATION,
  zapier: ZAPIER_INTEGRATION,
} satisfies Record<string, Integration>;

/** @knipignore */
export type INTEGRATION_ID = keyof typeof INTEGRATION_ID_TO_CONFIGURATION;

export const INTEGRATIONS: Integration[] = [
  {
    type: "external",
    name: "Squarespace",
    brandIcon: SquarespaceIcon,
    description:
      "Embed a Buttondown subscription form directly on your Squarespace site.",
    href: "https://buttondown.com/blog/2019-10-24-adding-a-buttondown-subscription-form-to-squarespace",
  },
  {
    type: "external",
    name: "Webflow",
    brandIcon: WebflowIcon,
    description:
      "Build a custom newsletter landing page with a subscription form in Webflow.",
    href: "https://buttondown.com/blog/2019-10-11-adding-a-buttondown-subscription-form-to-webflow",
  },
  // TODO: Re-enable Make integration once it's live.
  // {
  //   type: "external",
  //   name: "Make",
  //   brandIcon: MakeIcon,
  //   description:
  //     "Automate workflows by connecting Buttondown to thousands of apps.",
  //   href: "https://www.make.com/en/integrations/buttondown",
  // },
  ...Object.values(INTEGRATION_ID_TO_CONFIGURATION),
];

INTEGRATIONS.sort((a, b) => {
  if (a.name < b.name) {
    return -1;
  } else if (a.name > b.name) {
    return 1;
  } else {
    return 0;
  }
});
